import { FC, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Link2, Search, Plus, RefreshCw, X, Check, ChevronDown,
  Package, Store, Layers, Image, History, AlertTriangle,
  CheckSquare, Square, Trash2, ArrowRightLeft, Edit2,
  Zap, SortDesc, Download, DollarSign, Wand2,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PriceWidget from '../components/smart-analytics/PriceWidget';
import { DateRangeFilter } from '../components/smart-analytics/DateRangeFilter';
import { CreateGroupModal } from '../components/groups/CreateGroupModal';
import { OwnFloorField } from '../components/groups/OwnFloorField';
import { MergeGroupsModal, MergeableGroup } from '../components/groups/MergeGroupsModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ProductWithStats {
  _id: string;
  Sku: string;
  name?: string;
  imgUri?: string;
  url?: string;
  store?: { _id: string; name: string };
  productGroup?: { _id: string; name?: string; stockAvailable?: number } | null;
  createdAt?: string;
  deliveredOrders: number;
  totalOrders: number;
  revenue: number;
  jumiaQuantity?: number | null;
}

// Alias kept for backwards compat within this file
type UnassociatedProduct = ProductWithStats;

interface GroupDetail {
  _id: string;
  name: string;
  imageUrl?: string;
  category?: string;
  notes?: string;
  supplier?: string;
  minStockAlert?: number;
  buyingRate?: number;
  transportFees?: number;
  storageFees?: number;
  packageFees?: number;
  jumiaCommission?: number;
  fraisTraitement?: number;
  minProfitOverride?: number | null;
  newMarketPrice?: number;
  stockAvailable?: number;  // Jumia seller-center stock
  manualStock?: number | null;   // Inventory Control manual count
  damagedStock?: number;
  skuCount: number;
  deliveredOrders?: number;
  totalOrders?: number;
  revenue?: number;
  sampleSkus: { _id: string; Sku: string; name?: string; imgUri?: string; url?: string; store?: { name: string } }[];
}

interface Suggestion {
  group: { _id: string; name: string; imageUrl?: string };
  score: number;
  confidence: 'High' | 'Medium' | 'Low';
}

interface Toast {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const confidenceColor: Record<string, string> = {
  High:   'bg-emerald-500/15 text-emerald-600 border-emerald-500/20',
  Medium: 'bg-amber-500/15 text-amber-600 border-amber-500/20',
  Low:    'bg-slate-500/15 text-slate-500 border-slate-500/20',
};

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── Sub-components ──────────────────────────────────────────────────────────

const ProductImage: FC<{ src?: string; alt?: string; size?: number }> = ({ src, alt, size = 56 }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div
        style={{ width: size, height: size, minWidth: size }}
        className="flex items-center justify-center rounded-lg bg-muted border border-border"
      >
        <Image className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={src} alt={alt || 'Product'} onError={() => setErr(true)}
      style={{ width: size, height: size, minWidth: size }}
      className="rounded-lg object-cover border border-border bg-muted"
    />
  );
};

// ─── Assign Modal ─────────────────────────────────────────────────────────────

interface AssignModalProps {
  products: UnassociatedProduct[];
  groups: GroupDetail[];
  onConfirm: (groupId: string, groupName: string) => void;
  onClose: () => void;
}

const AssignModal: FC<AssignModalProps> = ({ products, groups, onConfirm, onClose }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GroupDetail | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = search.trim()
    ? groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
    : groups;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-dropdown overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold text-foreground">Assign to Product Group</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {products.length === 1
                ? `SKU: ${products[0].Sku}`
                : `${products.length} SKUs selected`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search groups…"
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Group list */}
        <div className="max-h-72 overflow-y-auto p-2 space-y-1">
          {filtered.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No groups found</p>
          )}
          {filtered.map(g => (
            <button
              key={g._id}
              onClick={() => setSelected(selected?._id === g._id ? null : g)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-150 text-left',
                selected?._id === g._id
                  ? 'border-brand bg-brand/10'
                  : 'border-transparent hover:border-border hover:bg-accent'
              )}
            >
              <ProductImage src={g.imageUrl} size={40} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{g.name}</p>
                <p className="text-xs text-muted-foreground">
                  {g.skuCount} SKU{g.skuCount !== 1 ? 's' : ''}
                  {g.sampleSkus.length > 0 && (
                    <span className="ml-1 opacity-70">· {g.sampleSkus.slice(0, 2).map(s => s.Sku).join(', ')}{g.skuCount > 2 ? '…' : ''}</span>
                  )}
                </p>
              </div>
              {selected?._id === g._id && <Check className="h-4 w-4 text-brand shrink-0" />}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            size="sm"
            className="flex-1 bg-brand hover:bg-brand-dark text-white"
            disabled={!selected}
            onClick={() => selected && onConfirm(selected._id, selected.name)}
          >
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Assign
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Group Detail Panel ───────────────────────────────────────────────────────

interface GroupDetailPanelProps {
  group: GroupDetail;
  onClose: () => void;
  onRemoveSku: (productId: string) => void;
  onMoveSku: (productId: string) => void;
  onSetImageFromSku: (groupId: string, productId: string) => void;
  onGroupUpdated: (updated: GroupDetail) => void;
  token: string;
}

const GroupDetailPanel: FC<GroupDetailPanelProps> = ({ group, onClose, onRemoveSku, onMoveSku, onSetImageFromSku, onGroupUpdated, token }) => {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({
    name:            group.name,
    category:        group.category        || '',
    notes:           group.notes           || '',
    supplier:        group.supplier        || '',
    minStockAlert:   group.minStockAlert   ?? 0,
    buyingRate:      group.buyingRate      ?? 0,
    transportFees:   group.transportFees   ?? 0,
    storageFees:     group.storageFees     ?? 0,
    packageFees:     group.packageFees     ?? 0,
    jumiaCommission: group.jumiaCommission ?? 15,
    fraisTraitement: group.fraisTraitement ?? 6,
    stockAvailable:  group.stockAvailable  ?? 0,
    newMarketPrice:  group.newMarketPrice  ?? 0,
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const totalCost = (form.buyingRate || 0) + (form.transportFees || 0) + (form.storageFees || 0) + (form.packageFees || 0);

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(actions.editProductGroup, form, token, { id: group._id });
    setSaving(false);
    if (result?._id) {
      onGroupUpdated({ ...group, ...form });
      setEditing(false);
    }
  };

  const FeeRow: FC<{ label: string; k: keyof typeof form }> = ({ label, k }) => (
    editing ? (
      <div className="space-y-0.5">
        <label className="text-[10px] font-semibold text-muted-foreground">{label}</label>
        <input type="number" min={0} step={0.1} value={form[k] as number}
          onChange={e => set(k, parseFloat(e.target.value) || 0)}
          className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
      </div>
    ) : (
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs font-semibold text-foreground">{((form[k] as number) || 0).toFixed(2)} MAD</span>
      </div>
    )
  );

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" />
      <div className="w-full max-w-sm h-full bg-card border-l border-border shadow-dropdown overflow-y-auto flex flex-col animate-fade-in"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-border sticky top-0 bg-card z-10">
          {editing ? (
            <input autoFocus value={form.name} onChange={e => set('name', e.target.value)}
              className="flex-1 mr-2 rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm font-bold outline-none focus:border-ring" />
          ) : (
            <h2 className="text-sm font-bold text-foreground truncate mr-2">{group.name}</h2>
          )}
          <div className="flex gap-1 shrink-0">
            {editing ? (
              <>
                <button onClick={() => { setEditing(false); setForm({ name: group.name, category: group.category||'', notes: group.notes||'', supplier: group.supplier||'', minStockAlert: group.minStockAlert??0, buyingRate: group.buyingRate??0, transportFees: group.transportFees??0, storageFees: group.storageFees??0, packageFees: group.packageFees??0, jumiaCommission: group.jumiaCommission??15, fraisTraitement: group.fraisTraitement??6, stockAvailable: group.stockAvailable??0, newMarketPrice: group.newMarketPrice??0 }); }}
                  className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="h-4 w-4" /></button>
                <button onClick={handleSave} disabled={saving}
                  className="p-1.5 rounded-lg bg-brand/10 hover:bg-brand/20 transition-colors text-brand disabled:opacity-50">
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} title="Edit group"
                  className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><Edit2 className="h-4 w-4" /></button>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="h-4 w-4" /></button>
              </>
            )}
          </div>
        </div>

        {/* Group image + meta */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex gap-3">
            <ProductImage src={group.imageUrl} size={72} />
            <div className="flex-1 space-y-1.5 text-xs min-w-0">
              {editing ? (
                <>
                  <input value={form.category} onChange={e => set('category', e.target.value)} placeholder="Category"
                    className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
                  <input value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="Supplier"
                    className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
                </>
              ) : (
                <>
                  {form.category && <p className="text-muted-foreground"><span className="font-semibold text-foreground">Category:</span> {form.category}</p>}
                  {form.supplier && <p className="text-muted-foreground"><span className="font-semibold text-foreground">Supplier:</span> {form.supplier}</p>}
                </>
              )}
              <Badge variant="secondary">{group.skuCount} SKU{group.skuCount !== 1 ? 's' : ''}</Badge>
            </div>
          </div>
          {editing && (
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Notes…"
              className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs outline-none focus:border-ring resize-none" />
          )}
          {!editing && form.notes && (
            <p className="text-xs text-muted-foreground italic">{form.notes}</p>
          )}
        </div>

        {/* Fees section */}
        <div className="p-4 border-b border-border space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <DollarSign className="h-3 w-3" /> Cost per unit (MAD)
          </p>
          <div className={editing ? 'grid grid-cols-2 gap-2' : 'space-y-1.5'}>
            <FeeRow label="Buying Rate"    k="buyingRate" />
            <FeeRow label="Inbound transport" k="transportFees" />
            <FeeRow label="Hub delivery cost" k="storageFees" />
            <FeeRow label="Packaging"         k="packageFees" />
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-border">
            <span className="text-xs font-semibold text-muted-foreground">Total cost</span>
            <span className="text-sm font-bold text-foreground">{totalCost.toFixed(2)} MAD</span>
          </div>
          <div className="pt-2 border-t border-border">
            <OwnFloorField groupId={group._id} initial={group.minProfitOverride} token={token} />
          </div>
          <div className="pt-1 border-t border-border">
            {editing ? (
              <div className="space-y-0.5">
                <label className="text-[10px] font-semibold text-muted-foreground">New Market Price (MAD)</label>
                <input type="number" min={0} step={0.01} value={form.newMarketPrice}
                  onChange={e => set('newMarketPrice', parseFloat(e.target.value) || 0)}
                  className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground">New Market Price</span>
                <span className="text-sm font-bold text-brand">{((form.newMarketPrice as number) || 0).toFixed(2)} MAD</span>
              </div>
            )}
          </div>

          {/* Jumia fees — not included in totalCost */}
          <div className="pt-2 mt-1 border-t border-dashed border-border space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Jumia Fees — not auto-calculated
            </p>
            <div className={editing ? 'grid grid-cols-2 gap-2' : 'space-y-1.5'}>
              {editing ? (
                <>
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-semibold text-muted-foreground">Commission Jumia (%)</label>
                    <input type="number" min={0} max={100} step={0.1} value={form.jumiaCommission}
                      onChange={e => set('jumiaCommission', parseFloat(e.target.value) || 0)}
                      className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
                  </div>
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-semibold text-muted-foreground">Frais de traitement (DH)</label>
                    <input type="number" min={0} step={0.1} value={form.fraisTraitement}
                      onChange={e => set('fraisTraitement', parseFloat(e.target.value) || 0)}
                      className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Commission Jumia</span>
                    <span className="text-xs font-semibold text-foreground">{(form.jumiaCommission ?? 15).toFixed(1)} %</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Frais de traitement</span>
                    <span className="text-xs font-semibold text-foreground">{(form.fraisTraitement ?? 6).toFixed(2)} MAD</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {editing && (
            <div className="space-y-0.5 pt-1">
              <label className="text-[10px] font-semibold text-muted-foreground">Stock Available</label>
              <input type="number" min={0} value={form.stockAvailable}
                onChange={e => set('stockAvailable', parseInt(e.target.value) || 0)}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs outline-none focus:border-ring" />
            </div>
          )}
        </div>

        {/* SKU list */}
        <div className="flex-1 p-3 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 pt-1">SKUs in this group</p>
          {group.sampleSkus.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">No SKUs yet</p>
          )}
          {group.sampleSkus.map(s => (
            <div key={s._id} className="flex items-center gap-2.5 p-2.5 rounded-xl border border-border hover:bg-accent/50 transition-colors group">
              <ProductImage src={s.imgUri} size={36} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">
                  {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.Sku}</a> : s.Sku}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">{s.name || '—'}</p>
                {s.store?.name && <span className="text-[10px] text-brand font-medium">{s.store.name}</span>}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {s.imgUri && (
                  <button onClick={() => onSetImageFromSku(group._id, s._id)} title="Use as group image"
                    className="p-1 rounded hover:bg-brand/10 text-muted-foreground hover:text-brand transition-colors">
                    <Image className="h-3 w-3" />
                  </button>
                )}
                <button onClick={() => onMoveSku(s._id)} title="Move to another group"
                  className="p-1 rounded hover:bg-brand/10 text-muted-foreground hover:text-brand transition-colors">
                  <ArrowRightLeft className="h-3 w-3" />
                </button>
                <button onClick={() => onRemoveSku(s._id)} title="Remove from group"
                  className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
          {group.skuCount > 5 && (
            <p className="text-xs text-muted-foreground text-center py-2">+ {group.skuCount - 5} more SKUs</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── SKU Card ─────────────────────────────────────────────────────────────────

interface SkuCardProps {
  product: ProductWithStats;
  selected: boolean;
  suggestions: Suggestion[];
  isAssociated?: boolean;
  groupLabel?: string;    // group name badge shown in search-all mode
  periodLabel?: string;   // e.g. "30d", "all time"
  groupJumiaStock?: number;
  groupManualStock?: number | null;
  buyingRate?: number;
  groupOurFees?: number;  // transport + storage + packaging from the group
  token: string;
  onToggleSelect: () => void;
  onAssign: () => void;
  onCreateGroup: () => void;
  onAssignSuggestion: (groupId: string, groupName: string) => void;
  onMove?: () => void;
  onRemove?: () => void;
}

const SkuCard: FC<SkuCardProps> = ({
  product, selected, suggestions, isAssociated, groupLabel, periodLabel, groupJumiaStock, groupManualStock, buyingRate, groupOurFees, token, onToggleSelect, onAssign, onCreateGroup, onAssignSuggestion, onMove, onRemove
}) => {
  const imgRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  const handleImgEnter = () => {
    if (!product.imgUri || !imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    const popupW = 220;
    const left = r.right + 8 + popupW > window.innerWidth ? r.left - popupW - 8 : r.right + 8;
    const top  = Math.min(r.top, window.innerHeight - 260);
    setPopupPos({ top, left });
  };

  return (
  <div className={cn(
    'relative flex flex-col rounded-2xl border bg-card shadow-card transition-all duration-150 hover:shadow-card-md overflow-hidden',
    selected ? 'border-brand ring-2 ring-brand/20' : 'border-border'
  )}>
    {/* Selection checkbox */}
    <button
      onClick={onToggleSelect}
      className="absolute top-2.5 left-2.5 z-10 p-0.5 rounded-md bg-card/90 backdrop-blur-sm border border-border hover:border-brand transition-colors"
    >
      {selected
        ? <CheckSquare className="h-4 w-4 text-brand" />
        : <Square className="h-4 w-4 text-muted-foreground" />}
    </button>

    {/* Image */}
    <div ref={imgRef} className="relative h-36 bg-muted flex items-center justify-center overflow-hidden"
      onMouseEnter={handleImgEnter}
      onMouseLeave={() => setPopupPos(null)}
    >
      {product.imgUri
        ? <img src={product.imgUri} alt={product.name} className="w-full h-full object-cover cursor-zoom-in" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        : <Image className="h-10 w-10 text-muted-foreground/40" />}
      {product.store?.name && (
        <span className="absolute bottom-2 right-2 rounded-md bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 backdrop-blur-sm">
          {product.store.name}
        </span>
      )}
      {groupLabel && (
        <span className="absolute top-2 right-2 rounded-md bg-brand/90 text-white text-[10px] font-bold px-1.5 py-0.5 backdrop-blur-sm max-w-[70%] truncate">
          {groupLabel}
        </span>
      )}
      {!groupLabel && isAssociated === false && (
        <span className="absolute top-2 right-2 rounded-md bg-slate-500/70 text-white text-[10px] font-semibold px-1.5 py-0.5">
          No group
        </span>
      )}

      {/* Positioned image popup — appears beside the card */}
      {popupPos && product.imgUri && (
        <div
          className="animate-fade-in rounded-xl overflow-hidden shadow-dropdown border border-border bg-card pointer-events-none"
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left, width: 220, zIndex: 999 }}
        >
          <img
            src={product.imgUri}
            alt={product.name || product.Sku}
            style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block', background: 'hsl(var(--muted))' }}
          />
          <div className="px-3 py-2 border-t border-border">
            {product.name && <p className="text-xs font-semibold text-foreground truncate">{product.name}</p>}
            <p className="text-[10px] text-muted-foreground font-mono">{product.Sku}</p>
          </div>
        </div>
      )}
    </div>

    {/* Content */}
    <div className="flex-1 p-3 space-y-2">
      <div>
        <p className="text-xs font-bold text-foreground font-mono truncate">
          {product.url ? <a href={product.url} target="_blank" rel="noopener noreferrer">{product.Sku}</a> : product.Sku}
        </p>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
          {product.name || <span className="italic">No name</span>}
        </p>
      </div>

      {/* Order stats */}
      {(product.deliveredOrders > 0 || product.totalOrders > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          {product.deliveredOrders > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-500/10 rounded-full px-2 py-0.5">
              ✓ {product.deliveredOrders} delivered · {periodLabel ?? 'all time'}
            </span>
          )}
          {product.revenue > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-foreground/70 bg-muted rounded-full px-2 py-0.5">
              {Math.round(product.revenue)} MAD
            </span>
          )}
        </div>
      )}

      {/* Group stock (warehouse + group Jumia pool) */}
      {(groupJumiaStock !== undefined || groupManualStock !== undefined) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {groupJumiaStock !== undefined && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-500/10 rounded-full px-2 py-0.5">
              📦 {groupJumiaStock} group stock
            </span>
          )}
          {groupManualStock !== null && groupManualStock !== undefined && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 bg-violet-500/10 rounded-full px-2 py-0.5">
              🏭 {groupManualStock} warehouse
            </span>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">Added {fmt(product.createdAt)}</p>

      {/* Jumia price + profit or payout estimate */}
      {product.store?._id && (
        <div className="mt-1">
          <PriceWidget
            storeId={product.store._id}
            sku={product.Sku}
            token={token}
            showProfit
            buyingRate={buyingRate}
            ourFees={groupOurFees}
          />
        </div>
      )}

      {/* Smart suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Zap className="h-2.5 w-2.5 text-brand" /> Suggestions
          </p>
          {suggestions.slice(0, 2).map((s, i) => (
            <button
              key={i}
              onClick={() => onAssignSuggestion(s.group._id, s.group.name)}
              className="w-full flex items-center gap-1.5 p-1.5 rounded-lg border border-border hover:border-brand hover:bg-brand/5 transition-all text-left group"
            >
              <ProductImage src={s.group.imageUrl} size={24} />
              <span className="flex-1 text-[11px] font-medium text-foreground truncate">{s.group.name}</span>
              <span className={cn('text-[9px] font-bold rounded-full border px-1.5 py-0.5 shrink-0', confidenceColor[s.confidence])}>
                {s.confidence}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Actions */}
    <div className="px-3 pb-3 flex gap-1.5">
      {isAssociated ? (
        <>
          <Button size="sm" variant="outline" onClick={onMove} className="flex-1 h-7 text-xs">
            <ArrowRightLeft className="h-3 w-3 mr-1" /> Move
          </Button>
          <Button size="sm" variant="outline" onClick={onRemove}
            className="flex-1 h-7 text-xs text-destructive hover:bg-destructive/5 border-destructive/30">
            <Trash2 className="h-3 w-3 mr-1" /> Remove
          </Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={onAssign} className="flex-1 h-7 text-xs">
            <Layers className="h-3 w-3 mr-1" /> Assign
          </Button>
          <Button size="sm" onClick={onCreateGroup}
            className="flex-1 h-7 text-xs bg-brand hover:bg-brand-dark text-white">
            <Plus className="h-3 w-3 mr-1" /> New Group
          </Button>
        </>
      )}
    </div>
  </div>
  );
};

// ─── History Drawer ───────────────────────────────────────────────────────────

interface HistoryEntry {
  _id: string;
  actionType: string;
  sku?: string;
  productName?: string;
  oldGroupName?: string;
  newGroupName?: string;
  changedBy?: string;
  bulkCount?: number;
  createdAt: string;
}

const actionLabel: Record<string, string> = {
  assign: 'Assigned',
  bulk_assign: 'Bulk assigned',
  remove: 'Removed',
  move: 'Moved',
  group_created: 'Group created',
  group_renamed: 'Group renamed',
  group_image_changed: 'Image changed',
  group_deleted: 'Group deleted',
};

const HistoryDrawer: FC<{ history: HistoryEntry[]; loading: boolean; onClose: () => void }> = ({ history, loading, onClose }) => (
  <div className="fixed inset-0 z-50 flex" onClick={onClose}>
    <div className="flex-1" />
    <div className="w-full max-w-sm h-full bg-card border-l border-border shadow-dropdown overflow-y-auto flex flex-col" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-4 border-b border-border sticky top-0 bg-card z-10">
        <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><History className="h-4 w-4 text-brand" /> Association History</h2>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
      </div>
      <div className="flex-1 p-3 space-y-2">
        {loading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
        {!loading && history.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No history yet</p>}
        {!loading && history.map(h => (
          <div key={h._id} className="p-3 rounded-xl border border-border bg-background space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Badge variant="secondary" className="text-[10px]">{actionLabel[h.actionType] || h.actionType}</Badge>
              <span className="text-[10px] text-muted-foreground">{fmt(h.createdAt)}</span>
            </div>
            {h.sku && <p className="text-xs font-mono font-semibold text-foreground">{h.sku}</p>}
            {h.bulkCount && <p className="text-xs text-muted-foreground">{h.bulkCount} SKUs</p>}
            <p className="text-[10px] text-muted-foreground">
              {h.oldGroupName && <span className="line-through mr-1">{h.oldGroupName}</span>}
              {h.newGroupName && <span className="text-brand font-medium">→ {h.newGroupName}</span>}
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SkuAssociationPage: FC = () => {
  const { contextStore } = useContext(ContextStore) as any;
  const token: string = contextStore?.token || '';

  const [products, setProducts]             = useState<ProductWithStats[]>([]);
  const [groups, setGroups]                 = useState<GroupDetail[]>([]);
  const [loading, setLoading]               = useState(true);
  const [refreshing, setRefreshing]         = useState(false);

  // Catalog scan state
  interface CatalogScanState {
    running: boolean; storesDone: number; storesTotal: number;
    currentStore: string | null; found: number; created: number;
    skipped: number; errors: number; finishedAt: string | null; lastError: string | null;
  }
  const [scanStatus,   setScanStatus]   = useState<CatalogScanState | null>(null);
  const [scanRunning,  setScanRunning]  = useState(false);
  const scanPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Main page tab
  const [mainTab, setMainTab]               = useState<'skus' | 'groups' | 'auto'>('skus');

  // ── Auto-Associate state ──────────────────────────────────────────────────
  interface PendingItem {
    _id: string; productId: string; sku: string; productName?: string; imgUri?: string;
    groupId: string; groupName: string; groupImageUrl?: string;
    score: number; confidence: 'High' | 'Medium'; matchedWords: string[]; status: string;
  }
  interface AAJobState {
    running: boolean; processed: number; total: number; matched: number;
    high: number; medium: number; startedAt: string | null; finishedAt: string | null;
    lastError: string | null; batchId: string | null;
  }
  const [aaJobState,      setAaJobState]      = useState<AAJobState | null>(null);
  const [aaJobRunning,    setAaJobRunning]     = useState(false);
  const aaPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [pendingItems,    setPendingItems]     = useState<PendingItem[]>([]);
  const [pendingHigh,     setPendingHigh]      = useState(0);
  const [pendingMedium,   setPendingMedium]    = useState(0);
  const [pendingLoading,  setPendingLoading]   = useState(false);
  const [aaConfTab,       setAaConfTab]        = useState<'High' | 'Medium'>('High');
  const [localDecisions,  setLocalDecisions]   = useState<Record<string, 'approved' | 'rejected'>>({});
  const [applying,        setApplying]         = useState(false);

  // Stats period filter — 'all' = no filter, 'custom' = use appliedFrom/appliedTo
  const [statsPeriod,  setStatsPeriod]  = useState<'all' | 'custom'>('all');
  const [customFrom,   setCustomFrom]   = useState('');
  const [customTo,     setCustomTo]     = useState('');
  const [appliedFrom,  setAppliedFrom]  = useState('');
  const [appliedTo,    setAppliedTo]    = useState('');

  const computeSince = () => (statsPeriod === 'custom' && appliedFrom ? new Date(appliedFrom).toISOString() : '');
  const computeUntil = () => {
    if (statsPeriod !== 'custom' || !appliedTo) return '';
    const d = new Date(appliedTo); d.setHours(23, 59, 59, 999); return d.toISOString();
  };
  const periodBadge = statsPeriod === 'all'
    ? 'all time'
    : `${appliedFrom || '…'} → ${appliedTo || '…'}`;

  // Group image popup (for the Product Groups tab)
  const [groupImgPopup, setGroupImgPopup] = useState<{ g: GroupDetail; top: number; left: number } | null>(null);

  const handleGroupImgEnter = (e: React.MouseEvent<HTMLDivElement>, g: GroupDetail) => {
    if (!g.imageUrl) return;
    const r = e.currentTarget.getBoundingClientRect();
    const popupW = 220;
    const left = r.right + 8 + popupW > window.innerWidth ? r.left - popupW - 8 : r.right + 8;
    setGroupImgPopup({ g, top: Math.min(r.top, window.innerHeight - 260), left });
  };

  // ── Smart clustering ─────────────────────────────────────────────────────
  interface SkuCluster { id: string; label: string; products: ProductWithStats[] }

  const STOP_WORDS = new Set([
    'pour','avec','sans','les','des','une','the','and','for','avec','type',
    'taille','couleur','pack','paire','pcs','lot','set','kit','noir','blanc',
    'rouge','bleu','vert','rose','gris','silver','gold','black','white','blue',
    'homme','femme','adulte','enfant','bebe','grand','petit','mini','ultra',
  ]);

  function extractClusterKey(name?: string): string {
    if (!name) return '?';
    const words = name
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')  // remove accents
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 3 && !STOP_WORDS.has(w) && !/^\d+$/.test(w));
    // Take up to 2 meaningful words as the cluster key
    return words.slice(0, 2).join(' ') || name.slice(0, 15).toLowerCase();
  }

  // SKU filters
  const [searchQuery, setSearchQuery]       = useState('');
  const [storeFilter, setStoreFilter]       = useState('');
  const [skuMode, setSkuMode]               = useState<'unassociated' | 'associated' | 'by-group' | 'search-all' | 'clusters'>('unassociated');
  const [sortOrder, setSortOrder]           = useState<'newest' | 'oldest' | 'sku' | 'orders' | 'revenue' | 'stock' | 'image' | 'keyword'>('orders');

  // By-group view
  const [filterGroupId, setFilterGroupId]   = useState<string>('');
  const [groupSkus, setGroupSkus]           = useState<ProductWithStats[]>([]);
  const [groupSkusLoading, setGroupSkusLoading] = useState(false);

  // Associated view
  const [associatedProducts, setAssociatedProducts] = useState<ProductWithStats[]>([]);
  const [associatedLoading,  setAssociatedLoading]  = useState(false);

  // Search-all view
  const [allSkusResults, setAllSkusResults] = useState<ProductWithStats[]>([]);
  const [allSkusLoading, setAllSkusLoading] = useState(false);
  const searchAllTimer                      = useRef<ReturnType<typeof setTimeout>>();

  // Group section filters
  const [groupSearch, setGroupSearch]       = useState('');
  const [groupSort, setGroupSort]           = useState<'name' | 'skuCount' | 'orders' | 'revenue' | 'manualStock' | 'jumiaStock'>('orders');

  // By-group searchable picker
  const [groupPickerOpen,   setGroupPickerOpen]   = useState(false);
  const [groupPickerSearch, setGroupPickerSearch] = useState('');
  const groupPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!groupPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (groupPickerRef.current && !groupPickerRef.current.contains(e.target as Node))
        setGroupPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [groupPickerOpen]);

  const [selectedIds, setSelectedIds]       = useState<Set<string>>(new Set());
  const [suggestionsMap, setSuggestionsMap] = useState<Record<string, Suggestion[]>>({});

  // Modals / panels
  const [assignTarget, setAssignTarget]     = useState<UnassociatedProduct[] | null>(null);
  const [createTarget, setCreateTarget]     = useState<UnassociatedProduct | UnassociatedProduct[] | null>(null);
  const [detailGroup, setDetailGroup]       = useState<GroupDetail | null>(null);
  const [moveTarget, setMoveTarget]         = useState<string | null>(null); // productId
  const [showHistory, setShowHistory]       = useState(false);
  const [history, setHistory]               = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [toasts, setToasts]                 = useState<Toast[]>([]);
  const toastId                             = useRef(0);

  // Group multi-select + merge
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [showMergeGroups, setShowMergeGroups]   = useState(false);

  // ── Toast helpers ─────────────────────────────────────────────────────────

  const addToast = useCallback((type: Toast['type'], message: string) => {
    const id = ++toastId.current;
    setToasts(t => [...t, { id, type, message }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    else setRefreshing(true);

    const since = computeSince();
    const until = computeUntil();
    const [prods, grps] = await Promise.all([
      dispatch(actions.getUnassociatedSkus, {}, token, { since, until }),
      dispatch(actions.getGroupsDetail, {}, token, { since, until }),
    ]);

    if (Array.isArray(prods)) setProducts(prods);
    if (Array.isArray(grps)) setGroups(grps);

    if (!quiet) setLoading(false);
    else setRefreshing(false);
  }, [token, statsPeriod, appliedFrom, appliedTo]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ── Catalog scan ──────────────────────────────────────────────────────────

  const stopScanPoll = () => {
    if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; }
  };

  const startScanPoll = useCallback(() => {
    stopScanPoll();
    scanPollRef.current = setInterval(async () => {
      const s = await dispatch(actions.getCatalogScanStatus, {}, token);
      if (s && !s.error) {
        setScanStatus(s);
        if (!s.running) {
          stopScanPoll();
          setScanRunning(false);
          // Reload unassociated list so new SKUs appear immediately
          load(true);
        }
      }
    }, 2500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => () => stopScanPoll(), []);

  const handleStartScan = useCallback(async () => {
    setScanRunning(true);
    const resp = await dispatch(actions.startCatalogScan, {}, token);
    if (resp?.queued) {
      startScanPoll();
    } else {
      setScanRunning(false);
      addToast('error', resp?.message || 'Could not start scan');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startScanPoll]);

  // Load initial scan status on mount
  useEffect(() => {
    (async () => {
      const s = await dispatch(actions.getCatalogScanStatus, {}, token);
      if (s && !s.error) {
        setScanStatus(s);
        if (s.running) { setScanRunning(true); startScanPoll(); }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load suggestions for visible products in batches
  useEffect(() => {
    if (products.length === 0) return;
    const toFetch = products.slice(0, 30).filter(p => !suggestionsMap[p._id]);
    if (!toFetch.length) return;

    (async () => {
      const results = await Promise.all(
        toFetch.map(p => dispatch(actions.getSkuSuggestions, {}, token, { productId: p._id })
          .then((r: Suggestion[]) => ({ id: p._id, suggestions: Array.isArray(r) ? r : [] }))
          .catch(() => ({ id: p._id, suggestions: [] }))
        )
      );
      setSuggestionsMap(prev => {
        const next = { ...prev };
        results.forEach(({ id, suggestions }) => { next[id] = suggestions; });
        return next;
      });
    })();
  }, [products, token]); // eslint-disable-line

  // ── Filtering / sorting ───────────────────────────────────────────────────

  const uniqueStores = Array.from(new Set(products.map(p => p.store?.name).filter(Boolean))) as string[];

  const sortProducts = (list: ProductWithStats[]) =>
    [...list].sort((a, b) => {
      if (sortOrder === 'sku')     return (a.Sku || '').localeCompare(b.Sku || '');
      if (sortOrder === 'oldest')  return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      if (sortOrder === 'orders')  return (b.deliveredOrders || 0) - (a.deliveredOrders || 0);
      if (sortOrder === 'revenue') return (b.revenue || 0) - (a.revenue || 0);
      if (sortOrder === 'stock')   return ((b.productGroup?.stockAvailable ?? 0) - (a.productGroup?.stockAvailable ?? 0));
      // Group by same image URL so product variants sit next to each other
      if (sortOrder === 'image')   return (a.imgUri || '').localeCompare(b.imgUri || '');
      // Group by keyword so similar product names sit next to each other
      if (sortOrder === 'keyword') return extractClusterKey(a.name).localeCompare(extractClusterKey(b.name));
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });

  const applySkuFilters = (list: ProductWithStats[]) =>
    list.filter(p => {
      if (storeFilter && p.store?.name !== storeFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (p.Sku || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q);
      }
      return true;
    });

  const smartClusters = useMemo((): SkuCluster[] => {
    const source = applySkuFilters(products);
    const byImg  = new Map<string, ProductWithStats[]>();
    const noImg: ProductWithStats[] = [];

    for (const p of source) {
      if (p.imgUri) {
        if (!byImg.has(p.imgUri)) byImg.set(p.imgUri, []);
        byImg.get(p.imgUri)!.push(p);
      } else {
        noImg.push(p);
      }
    }

    const clusters: SkuCluster[] = [];
    Array.from(byImg.entries()).forEach(([imgUri, prods]) => {
      const label = extractClusterKey(prods[0].name) || prods[0].Sku.slice(0, 20);
      clusters.push({ id: `img-${imgUri.slice(-20)}`, label, products: prods });
    });

    const byKw = new Map<string, ProductWithStats[]>();
    for (const p of noImg) {
      const kw = extractClusterKey(p.name);
      if (!byKw.has(kw)) byKw.set(kw, []);
      byKw.get(kw)!.push(p);
    }
    Array.from(byKw.entries()).forEach(([kw, prods]) => {
      clusters.push({ id: `kw-${kw}`, label: kw, products: prods });
    });

    return clusters.sort((a, b) =>
      b.products.length - a.products.length || a.label.localeCompare(b.label)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchQuery, storeFilter]);

  const visible = skuMode === 'by-group'
    ? sortProducts(applySkuFilters(groupSkus))
    : skuMode === 'search-all'
      ? sortProducts(allSkusResults)
      : skuMode === 'associated'
        ? sortProducts(applySkuFilters(associatedProducts))
        : sortProducts(applySkuFilters(products));

  // Sorted/filtered groups
  const visibleGroups = [...groups]
    .filter(g => !groupSearch.trim() || g.name.toLowerCase().includes(groupSearch.toLowerCase()))
    .sort((a, b) => {
      if (groupSort === 'name')       return (a.name || '').localeCompare(b.name || '');
      if (groupSort === 'skuCount')   return (b.skuCount || 0) - (a.skuCount || 0);
      if (groupSort === 'orders')     return (b.deliveredOrders || 0) - (a.deliveredOrders || 0);
      if (groupSort === 'revenue')    return (b.revenue || 0) - (a.revenue || 0);
      if (groupSort === 'manualStock') return ((b.manualStock ?? -1) - (a.manualStock ?? -1));
      if (groupSort === 'jumiaStock') return ((b.stockAvailable ?? 0) - (a.stockAvailable ?? 0));
      return (b.deliveredOrders || 0) - (a.deliveredOrders || 0);
    });

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggleSelect = (id: string) =>
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleAll = () =>
    setSelectedIds(prev => prev.size === visible.length ? new Set() : new Set(visible.map(p => p._id)));

  const selectedProducts = products.filter(p => selectedIds.has(p._id));

  // ── Actions ───────────────────────────────────────────────────────────────

  const doAssign = async (groupId: string, groupName: string, productsList: UnassociatedProduct[]) => {
    const result = await dispatch(actions.bulkAssignSkus, { productIds: productsList.map(p => p._id), groupId }, token);
    if (result?.error) { addToast('error', result.error.message); return; }
    addToast('success', `${productsList.length} SKU${productsList.length > 1 ? 's' : ''} assigned to "${groupName}"`);
    setSelectedIds(new Set());
    setAssignTarget(null);
    load(true);
    setGroups(prev => prev.map(g =>
      g._id === groupId ? { ...g, skuCount: g.skuCount + productsList.length } : g
    ));
  };

  const doRemoveSku = async (productId: string) => {
    await dispatch(actions.removeProduct, {}, token, { productId });
    addToast('info', 'SKU removed from group');
    if (detailGroup) {
      setDetailGroup(prev => prev
        ? { ...prev, sampleSkus: prev.sampleSkus.filter(s => s._id !== productId), skuCount: prev.skuCount - 1 }
        : null
      );
    }
    load(true);
  };

  const doMoveSku = (productId: string) => {
    setMoveTarget(productId);
    setDetailGroup(null);
  };

  const doMoveConfirm = async (groupId: string, groupName: string) => {
    if (!moveTarget) return;
    await dispatch(actions.moveSkuToGroup, { newGroupId: groupId }, token, { productId: moveTarget });
    addToast('success', `SKU moved to "${groupName}"`);
    setMoveTarget(null);
    load(true);
  };

  const loadGroupSkus = useCallback(async (groupId: string) => {
    if (!groupId) { setGroupSkus([]); return; }
    setGroupSkusLoading(true);
    const since = computeSince();
    const until = computeUntil();
    const data = await dispatch(actions.getSkusByGroup, {}, token, { groupId, since, until });
    setGroupSkus(Array.isArray(data) ? data : []);
    setGroupSkusLoading(false);
  }, [token, statsPeriod, appliedFrom, appliedTo]); // eslint-disable-line

  const loadAssociated = useCallback(async () => {
    setAssociatedLoading(true);
    const since = computeSince();
    const until = computeUntil();
    const data = await dispatch(actions.getAssociatedSkus, {}, token, { since, until });
    setAssociatedProducts(Array.isArray(data) ? data : []);
    setAssociatedLoading(false);
  }, [token, statsPeriod, appliedFrom, appliedTo]); // eslint-disable-line

  const handleSkuModeChange = (mode: 'unassociated' | 'associated' | 'by-group' | 'search-all' | 'clusters') => {
    setSkuMode(mode);
    setSelectedIds(new Set());
    if (mode !== 'by-group') setFilterGroupId('');
    if (mode !== 'search-all') setAllSkusResults([]);
    if (mode === 'associated' && associatedProducts.length === 0) loadAssociated();
  };

  // Reload associated when period changes while in that mode
  useEffect(() => {
    if (skuMode === 'associated') loadAssociated();
  }, [loadAssociated]); // eslint-disable-line

  const runSearchAll = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) { setAllSkusResults([]); return; }
    setAllSkusLoading(true);
    const since = computeSince();
    const until = computeUntil();
    const data = await dispatch(actions.searchAllSkus, {}, token, { search: q.trim(), storeId: storeFilter, since, until });
    setAllSkusResults(Array.isArray(data) ? data : []);
    setAllSkusLoading(false);
  }, [token, storeFilter, statsPeriod, appliedFrom, appliedTo]); // eslint-disable-line

  // Debounced search-all when in that mode
  useEffect(() => {
    if (skuMode !== 'search-all') return;
    clearTimeout(searchAllTimer.current);
    searchAllTimer.current = setTimeout(() => runSearchAll(searchQuery), 350);
    return () => clearTimeout(searchAllTimer.current);
  }, [searchQuery, skuMode, runSearchAll]);

  const handleFilterGroupChange = (groupId: string) => {
    setFilterGroupId(groupId);
    loadGroupSkus(groupId);
  };

  // Reload group SKUs when period changes while in by-group mode
  useEffect(() => {
    if (skuMode === 'by-group' && filterGroupId) loadGroupSkus(filterGroupId);
  }, [loadGroupSkus]); // eslint-disable-line

  const doSetImageFromSku = async (groupId: string, productId: string) => {
    const result = await dispatch(actions.setGroupImageFromSku, {}, token, { groupId, productId });
    if (result?.error) { addToast('error', result.error.message); return; }
    addToast('success', 'Group image updated');
    load(true);
    if (detailGroup?._id === groupId) {
      setDetailGroup(prev => prev ? { ...prev, imageUrl: result.imageUrl } : null);
    }
  };

  const doAutoFixImages = async () => {
    addToast('info', 'Auto-fixing group images…');
    const result = await dispatch(actions.autoFixGroupImages, {}, token);
    if (result?.error) { addToast('error', result.error.message); return; }
    addToast('success', `${result.updated} group${result.updated !== 1 ? 's' : ''} updated with images from their SKUs`);
    load(true);
  };

  // ── Auto-Associate functions ──────────────────────────────────────────────

  const stopAaPoll = () => {
    if (aaPollRef.current) { clearInterval(aaPollRef.current); aaPollRef.current = null; }
  };

  const loadPendingItems = useCallback(async (conf: 'High' | 'Medium') => {
    setPendingLoading(true);
    const r = await dispatch(actions.getPendingAssociations, {}, token, { confidence: conf, page: 0, limit: 100 });
    if (r && !r.error) {
      setPendingItems(Array.isArray(r.items) ? r.items : []);
      setPendingHigh(r.highCount ?? 0);
      setPendingMedium(r.mediumCount ?? 0);
    }
    setPendingLoading(false);
  }, [token]); // eslint-disable-line

  const startAaPoll = useCallback(() => {
    stopAaPoll();
    aaPollRef.current = setInterval(async () => {
      const s = await dispatch(actions.getAutoAssociateStatus, {}, token);
      if (!s) return;
      setAaJobState(s);
      if (!s.running) {
        stopAaPoll();
        setAaJobRunning(false);
        if (!s.lastError) {
          loadPendingItems('High');
          addToast('success', `Auto-Associate done — ${s.high} High, ${s.medium} Medium suggestions ready`);
        } else {
          addToast('error', `Auto-Associate failed: ${s.lastError}`);
        }
      }
    }, 1200);
  }, [token, loadPendingItems]); // eslint-disable-line

  const handleStartAutoAssociate = async () => {
    const r = await dispatch(actions.startAutoAssociate, {}, token);
    if (r?.error) { addToast('error', r.error.message); return; }
    setAaJobRunning(true);
    setLocalDecisions({});
    setPendingItems([]);
    addToast('info', 'Auto-Associate started — analysing your SKUs…');
    startAaPoll();
  };

  const toggleDecision = (id: string, action: 'approved' | 'rejected') => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      if (next[id] === action) delete next[id]; // toggle off
      else next[id] = action;
      return next;
    });
  };

  const acceptAllVisible = () => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      pendingItems.filter(i => i.confidence === aaConfTab).forEach(i => { next[i._id] = 'approved'; });
      return next;
    });
  };

  const rejectAllVisible = () => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      pendingItems.filter(i => i.confidence === aaConfTab).forEach(i => { next[i._id] = 'rejected'; });
      return next;
    });
  };

  const applyDecisions = async () => {
    const decisions = Object.entries(localDecisions).map(([id, action]) => ({ id, action }));
    if (!decisions.length) { addToast('info', 'No decisions to apply yet'); return; }
    setApplying(true);
    const r = await dispatch(actions.applyPendingAssociations, { decisions, changedBy: 'user' }, token);
    setApplying(false);
    if (r?.error) { addToast('error', r.error.message); return; }
    addToast('success', `Applied: ${r.appliedCount} assigned, ${r.rejectedCount} rejected`);
    setLocalDecisions({});
    loadPendingItems(aaConfTab);
    load(true); // refresh the main unassociated list
  };

  const clearPending = async () => {
    await dispatch(actions.clearPendingAssociations, {}, token);
    setPendingItems([]); setPendingHigh(0); setPendingMedium(0);
    setLocalDecisions({});
    addToast('info', 'Pending suggestions cleared');
  };

  // Load pending counts when switching to auto tab
  useEffect(() => {
    if (mainTab === 'auto') loadPendingItems(aaConfTab);
  }, [mainTab]); // eslint-disable-line

  useEffect(() => {
    if (mainTab === 'auto') loadPendingItems(aaConfTab);
  }, [aaConfTab]); // eslint-disable-line

  useEffect(() => { return () => stopAaPoll(); }, []); // cleanup on unmount

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    const h = await dispatch(actions.getAssociationHistory, {}, token, { limit: 80 });
    setHistory(Array.isArray(h) ? h : []);
    setHistoryLoading(false);
  };

  const exportCsv = () => {
    const rows = [['SKU', 'Name', 'Store', 'Added'].join(',')];
    products.forEach(p => {
      rows.push([p.Sku, `"${(p.name || '').replace(/"/g, '""')}"`, p.store?.name || '', fmt(p.createdAt)].join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'unassociated-skus.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full bg-background">

      {/* ── Toast stack ──────────────────────────────────────────── */}
      <div className="fixed bottom-4 right-4 z-[200] space-y-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={cn(
            'flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium shadow-dropdown border animate-fade-in pointer-events-auto',
            t.type === 'success' && 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
            t.type === 'error'   && 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-300',
            t.type === 'info'    && 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
          )}>
            {t.type === 'success' && <Check className="h-3.5 w-3.5 shrink-0" />}
            {t.type === 'error'   && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
            {t.type === 'info'    && <Package className="h-3.5 w-3.5 shrink-0" />}
            {t.message}
          </div>
        ))}
      </div>

      {/* ── Page header ──────────────────────────────────────────── */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="px-4 lg:px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2.5 mr-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand/10">
              <Link2 className="h-4 w-4 text-brand" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-foreground leading-tight">SKU Association Center</h1>
              <p className="text-[10px] text-muted-foreground leading-tight">
                {loading ? 'Loading…' : `${products.length} unassociated · ${groups.length} groups`}
              </p>
            </div>
          </div>

          {/* ── Main page tabs ── */}
          <div className="flex rounded-lg border border-input bg-muted p-0.5 gap-0.5">
            {([
              ['skus',   'SKU Association'],
              ['groups', 'Product Groups'],
            ] as const).map(([tab, label]) => (
              <button key={tab} onClick={() => { setMainTab(tab as any); setSelectedGroupIds(new Set()); }}
                className={cn('rounded-md px-3 py-1 text-xs font-semibold transition-all',
                  mainTab === tab ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                )}>
                {label}
              </button>
            ))}
            <button
              onClick={() => { setMainTab('auto'); setSelectedGroupIds(new Set()); }}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-semibold transition-all flex items-center gap-1',
                mainTab === 'auto' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Wand2 className="h-3 w-3" />
              Auto-Associate
              {(pendingHigh + pendingMedium) > 0 && (
                <span className="ml-0.5 rounded-full bg-brand text-white text-[9px] font-bold px-1.5 py-0.5 leading-none">
                  {pendingHigh + pendingMedium}
                </span>
              )}
            </button>
          </div>

          <div className="flex-1" />

          <Button variant="ghost" size="icon-sm" onClick={doAutoFixImages} title="Auto-fill missing group images from their SKUs">
            <Wand2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={openHistory} title="History">
            <History className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={exportCsv} title="Export CSV">
            <Download className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => load(true)} disabled={refreshing} title="Refresh">
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </Button>
          <Button
            variant="outline" size="sm"
            className={cn('gap-1.5 text-xs', scanRunning && 'border-emerald-500 text-emerald-600')}
            onClick={handleStartScan}
            disabled={scanRunning}
            title="Scan your Jumia catalog and add any SKUs missing from this system"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', scanRunning && 'animate-spin')} />
            {scanRunning ? 'Scanning…' : 'Scan Catalog'}
          </Button>
          <Button size="sm" className="bg-brand hover:bg-brand-dark text-white"
            onClick={() => setCreateTarget([])}>
            <Plus className="h-3.5 w-3.5 mr-1" /> New Group
          </Button>
        </div>

        {/* ── Catalog scan progress banner ─────────────────────── */}
        {(scanRunning || (scanStatus && scanStatus.finishedAt && !scanStatus.running && scanStatus.created > 0)) && (
          <div className={cn(
            'px-4 lg:px-6 py-2 flex items-center gap-3 text-xs flex-wrap',
            scanRunning ? 'bg-emerald-50 border-b border-emerald-100' : 'bg-muted border-b border-border'
          )}>
            <RefreshCw className={cn('h-3.5 w-3.5 shrink-0', scanRunning ? 'animate-spin text-emerald-600' : 'text-muted-foreground')} />
            {scanRunning && scanStatus ? (
              <>
                <span className="font-semibold text-emerald-700">
                  Scanning {scanStatus.currentStore || '…'}
                </span>
                <span className="text-muted-foreground">
                  {scanStatus.storesDone}/{scanStatus.storesTotal} stores ·{' '}
                  {scanStatus.created} new SKUs added · {scanStatus.found} found so far
                </span>
                {/* Progress bar across all stores */}
                {scanStatus.storesTotal > 0 && (
                  <div className="flex-1 min-w-24 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${Math.round((scanStatus.storesDone / scanStatus.storesTotal) * 100)}%` }}
                    />
                  </div>
                )}
              </>
            ) : scanStatus && !scanStatus.running && scanStatus.created > 0 ? (
              <span className="text-muted-foreground">
                Last scan: <strong className="text-foreground">{scanStatus.created} new SKUs</strong> added from{' '}
                {scanStatus.found} catalog entries across {scanStatus.storesTotal} store{scanStatus.storesTotal !== 1 ? 's' : ''}.
              </span>
            ) : null}
          </div>
        )}

        {/* ── Filter bar ───────────────────────────────────────── */}
        {mainTab === 'skus' && <div className="px-4 lg:px-6 pb-3 space-y-2">
          {/* Mode tabs + search row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Mode tabs */}
            <div className="flex rounded-lg border border-input bg-muted p-0.5 gap-0.5">
              {([
                ['unassociated', 'Unassociated'],
                ['clusters',     '✦ Smart Clusters'],
                ['associated',   'Associated'],
                ['search-all',   'Search All SKUs'],
                ['by-group',     'By Group'],
              ] as const).map(([mode, label]) => (
                <button key={mode} onClick={() => handleSkuModeChange(mode)}
                  className={cn('rounded-md px-3 py-1 text-xs font-semibold transition-all',
                    skuMode === mode ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}>
                  {label}
                </button>
              ))}
            </div>

            {/* Group picker (only in by-group mode) — searchable */}
            {skuMode === 'by-group' && (
              <div ref={groupPickerRef} className="relative">
                <button
                  onClick={() => { setGroupPickerOpen(o => !o); setGroupPickerSearch(''); }}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border bg-background pl-3 pr-2 py-1.5 text-sm outline-none cursor-pointer max-w-[240px] min-w-[180px]',
                    groupPickerOpen ? 'border-ring ring-2 ring-ring/20' : 'border-input'
                  )}
                >
                  <span className="flex-1 text-left truncate text-sm">
                    {filterGroupId
                      ? (groups.find(g => g._id === filterGroupId)?.name ?? '— Select a group —')
                      : '— Select a group —'}
                  </span>
                  {filterGroupId && (
                    <X className="h-3 w-3 text-muted-foreground shrink-0"
                      onClick={e => { e.stopPropagation(); handleFilterGroupChange(''); setGroupPickerOpen(false); }} />
                  )}
                  <ChevronDown className={cn('h-3 w-3 text-muted-foreground shrink-0 transition-transform', groupPickerOpen && 'rotate-180')} />
                </button>

                {groupPickerOpen && (
                  <div className="absolute top-[calc(100%+6px)] left-0 z-[200] bg-card border border-border rounded-xl shadow-dropdown overflow-hidden min-w-[260px]">
                    {/* Search input */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/40">
                      <Search className="h-3 w-3 text-muted-foreground shrink-0" />
                      <input
                        autoFocus
                        value={groupPickerSearch}
                        onChange={e => setGroupPickerSearch(e.target.value)}
                        placeholder="Search group…"
                        className="flex-1 text-xs bg-transparent outline-none placeholder:text-muted-foreground text-foreground"
                      />
                      {groupPickerSearch && (
                        <button onClick={() => setGroupPickerSearch('')}>
                          <X className="h-3 w-3 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                    {/* List */}
                    <div className="max-h-64 overflow-y-auto p-1">
                      {groups
                        .filter(g => !groupPickerSearch.trim() || g.name.toLowerCase().includes(groupPickerSearch.toLowerCase()))
                        .map(g => (
                          <button
                            key={g._id}
                            onClick={() => { handleFilterGroupChange(g._id); setGroupPickerOpen(false); setGroupPickerSearch(''); }}
                            className={cn(
                              'flex items-center justify-between w-full rounded-lg px-3 py-2 text-left text-xs transition-colors gap-2',
                              filterGroupId === g._id
                                ? 'bg-brand/10 text-brand font-semibold'
                                : 'text-foreground hover:bg-muted'
                            )}
                          >
                            <span className="truncate flex-1">{g.name}</span>
                            <span className={cn(
                              'text-[10px] rounded-full px-1.5 py-0.5 shrink-0 font-medium',
                              filterGroupId === g._id ? 'bg-brand/20 text-brand' : 'bg-muted text-muted-foreground'
                            )}>
                              {g.skuCount}
                            </span>
                          </button>
                        ))}
                      {groups.filter(g => !groupPickerSearch.trim() || g.name.toLowerCase().includes(groupPickerSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-4">No groups found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SKU search */}
            <div className={cn(
              "flex items-center gap-2 rounded-lg border bg-background px-3 py-1.5 min-w-[200px] flex-1 max-w-sm transition-colors",
              skuMode === 'search-all' ? "border-brand ring-2 ring-brand/20" : "border-input"
            )}>
              {allSkusLoading
                ? <RefreshCw className="h-3.5 w-3.5 text-brand animate-spin shrink-0" />
                : <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={skuMode === 'search-all' ? "Type SKU or name to search all…" : "Search SKU or name…"}
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground" />
              {searchQuery && <button onClick={() => { setSearchQuery(''); setAllSkusResults([]); }}><X className="h-3 w-3 text-muted-foreground" /></button>}
            </div>

            {/* Store filter */}
            {uniqueStores.length > 1 && (
              <div className="relative">
                <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}
                  className="appearance-none rounded-lg border border-input bg-background pl-3 pr-7 py-1.5 text-sm outline-none focus:border-ring cursor-pointer text-foreground">
                  <option value="">All Stores</option>
                  {uniqueStores.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
              </div>
            )}

            {/* SKU sort */}
            <div className="relative">
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value as any)}
                className="appearance-none rounded-lg border border-input bg-background pl-3 pr-7 py-1.5 text-sm outline-none focus:border-ring cursor-pointer text-foreground">
                <option value="image">By image (similar together)</option>
                <option value="keyword">By keyword (similar names together)</option>
                <option value="orders">Orders ↓</option>
                <option value="revenue">Revenue ↓</option>
                <option value="stock">Stock ↓</option>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="sku">SKU A–Z</option>
              </select>
              <SortDesc className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
            </div>

            {/* Stats period filter */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setStatsPeriod('all'); setAppliedFrom(''); setAppliedTo(''); }}
                className={cn('rounded-md px-2.5 py-1 text-[11px] font-semibold border transition-all',
                  statsPeriod === 'all'
                    ? 'bg-foreground text-background border-foreground'
                    : 'border-input text-muted-foreground hover:text-foreground bg-background'
                )}>
                All time
              </button>
              <DateRangeFilter
                fromDate={customFrom}
                toDate={customTo}
                onFromChange={setCustomFrom}
                onToChange={setCustomTo}
                onApply={() => {
                  setAppliedFrom(customFrom);
                  setAppliedTo(customTo);
                  setStatsPeriod('custom');
                }}
              />
            </div>

            <div className="flex-1" />
            <span className="text-xs text-muted-foreground">{visible.length} shown</span>
          </div>
        </div>}

        {/* ── Bulk action bar ───────────────────────────────────────── */}
        {mainTab === 'skus' && selectedIds.size > 0 && (
          <div className="mx-4 lg:mx-6 mb-2 flex items-center gap-2 rounded-xl border border-brand bg-brand/5 px-4 py-2.5 shadow-card animate-fade-in">
            <span className="text-sm font-semibold text-brand">{selectedIds.size} selected</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs">Deselect all</Button>
            <Button size="sm" className="h-7 text-xs bg-brand hover:bg-brand-dark text-white"
              onClick={() => setAssignTarget(selectedProducts)}>
              <Layers className="h-3 w-3 mr-1" /> Assign to group
            </Button>
            <Button size="sm" className="h-7 text-xs bg-foreground hover:bg-foreground/90 text-background"
              onClick={() => setCreateTarget(selectedProducts)}>
              <Plus className="h-3 w-3 mr-1" /> Create new group
            </Button>
          </div>
        )}
      </div>

      <div className="px-4 lg:px-6 py-4">

        {/* ── SKU Association tab content ───────────────────────── */}
        {mainTab === 'skus' && <>

        {/* ── Smart Clusters view ──────────────────────────────── */}
        {skuMode === 'clusters' && !loading && (
          smartClusters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10">
                <Layers className="h-8 w-8 text-brand" />
              </div>
              <p className="text-sm text-muted-foreground">No unassociated SKUs to cluster</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span><strong className="text-foreground">{smartClusters.length}</strong> clusters from <strong className="text-foreground">{smartClusters.reduce((s, c) => s + c.products.length, 0)}</strong> SKUs</span>
                <span>·</span>
                <span>Same image = same product on Jumia · Similar name = same category</span>
              </div>

              {smartClusters.map(cluster => {
                const clusterIds = cluster.products.map(p => p._id);
                const allSelected = clusterIds.every(id => selectedIds.has(id));
                const someSelected = clusterIds.some(id => selectedIds.has(id));

                const toggleCluster = () => {
                  setSelectedIds(prev => {
                    const next = new Set(prev);
                    if (allSelected) clusterIds.forEach(id => next.delete(id));
                    else clusterIds.forEach(id => next.add(id));
                    return next;
                  });
                };

                return (
                  <div key={cluster.id} className={cn(
                    'rounded-2xl border bg-card overflow-hidden',
                    someSelected ? 'border-brand/40 ring-1 ring-brand/20' : 'border-border'
                  )}>
                    {/* Cluster header */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-muted/40 border-b border-border">
                      {/* Thumbnail strip — up to 4 unique images */}
                      <div className="flex -space-x-2 shrink-0">
                        {cluster.products.slice(0, 4).map((p, i) => (
                          <div key={i} className="h-9 w-9 rounded-lg border-2 border-background bg-muted overflow-hidden shrink-0">
                            {p.imgUri
                              ? <img src={p.imgUri} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              : <div className="w-full h-full flex items-center justify-center"><Package className="h-4 w-4 text-muted-foreground/40" /></div>}
                          </div>
                        ))}
                      </div>

                      {/* Label + count */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground capitalize truncate">{cluster.label}</p>
                        <p className="text-xs text-muted-foreground">{cluster.products.length} SKU{cluster.products.length !== 1 ? 's' : ''}</p>
                      </div>

                      {/* Select cluster toggle */}
                      <button
                        onClick={toggleCluster}
                        className={cn(
                          'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all shrink-0',
                          allSelected
                            ? 'bg-brand text-white border-brand'
                            : 'bg-background text-muted-foreground border-input hover:border-brand hover:text-brand'
                        )}
                      >
                        {allSelected
                          ? <><CheckSquare className="h-3.5 w-3.5" /> Deselect cluster</>
                          : <><Square className="h-3.5 w-3.5" /> Select cluster</>}
                      </button>
                    </div>

                    {/* SKU grid inside cluster */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 p-3">
                      {cluster.products.map(p => (
                        <SkuCard
                          key={p._id}
                          product={p}
                          selected={selectedIds.has(p._id)}
                          suggestions={suggestionsMap[p._id] || []}
                          periodLabel={periodBadge}
                          token={token}
                          onToggleSelect={() => toggleSelect(p._id)}
                          onAssign={() => setAssignTarget([p])}
                          onCreateGroup={() => setCreateTarget(p)}
                          onAssignSuggestion={(gid, gname) => doAssign(gid, gname, [p])}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ── Search-all prompt / loading ───────────────────────── */}
        {skuMode === 'search-all' && !searchQuery.trim() && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Search className="h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Type a SKU or product name to search across all products</p>
            <p className="text-xs text-muted-foreground/60">Finds both associated and unassociated SKUs</p>
          </div>
        )}
        {skuMode === 'search-all' && searchQuery.trim().length === 1 && (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <p className="text-sm text-muted-foreground">Keep typing… (minimum 2 characters)</p>
          </div>
        )}

        {/* ── By-group loading / empty ──────────────────────────── */}
        {skuMode === 'by-group' && !filterGroupId && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Layers className="h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Select a group above to browse its SKUs</p>
          </div>
        )}
        {skuMode === 'by-group' && filterGroupId && groupSkusLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
                <Skeleton className="h-36 w-full rounded-none" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-3 w-3/4" /><Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Loading skeletons ─────────────────────────────────── */}
        {associatedLoading && skuMode === 'associated' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
                <Skeleton className="h-36 w-full rounded-none" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-3 w-3/4" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}
        {loading && skuMode === 'unassociated' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
                <Skeleton className="h-36 w-full rounded-none" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Empty state ───────────────────────────────────────── */}
        {!loading && !associatedLoading && !groupSkusLoading && !allSkusLoading && visible.length === 0 &&
          ((skuMode === 'unassociated') ||
           (skuMode === 'associated') ||
           (skuMode === 'by-group' && filterGroupId) ||
           (skuMode === 'search-all' && searchQuery.trim().length >= 2)) && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10">
              <Link2 className="h-8 w-8 text-brand" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-bold text-foreground">
                {searchQuery || storeFilter ? 'No SKUs match your filters' : 'All SKUs are associated!'}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {searchQuery || storeFilter
                  ? 'Try a different search or clear the filters.'
                  : 'Every SKU is linked to a product group.'}
              </p>
            </div>
            {(searchQuery || storeFilter) && (
              <Button variant="outline" size="sm" onClick={() => { setSearchQuery(''); setStoreFilter(''); }}>
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* ── SKU Grid ──────────────────────────────────────────── */}
        {!loading && !associatedLoading && !groupSkusLoading && !allSkusLoading && visible.length > 0 &&
          (skuMode === 'unassociated' ||
           skuMode === 'associated' ||
           (skuMode === 'by-group' && filterGroupId) ||
           (skuMode === 'search-all' && searchQuery.trim().length >= 2)) && (
          <>
            {/* Select all row */}
            <div className="flex items-center gap-2 mb-3">
              <button onClick={toggleAll} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                {selectedIds.size === visible.length
                  ? <CheckSquare className="h-3.5 w-3.5 text-brand" />
                  : <Square className="h-3.5 w-3.5" />}
                Select all
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {visible.map(p => {
                const pg = (p.productGroup as any);
                const pgIsAssociated = !!pg?._id;
                const pgBuyingRate   = pgIsAssociated ? (pg.buyingRate || undefined) : undefined;
                const pgOurFees = pgIsAssociated ? (() => {
                  const t = (pg.transportFees||0) + (pg.storageFees||0) + (pg.packageFees||0);
                  return t > 0 ? t : undefined;
                })() : undefined;

                return (
                <SkuCard
                  key={p._id}
                  product={p}
                  selected={selectedIds.has(p._id)}
                  suggestions={skuMode === 'unassociated' ? (suggestionsMap[p._id] || []) : []}
                  isAssociated={skuMode === 'by-group' || skuMode === 'associated' || (skuMode === 'search-all' && pgIsAssociated)}
                  groupLabel={
                    (skuMode === 'search-all' && pgIsAssociated) || skuMode === 'associated'
                      ? (pg?.name as string ?? undefined)
                      : undefined
                  }
                  periodLabel={periodBadge}
                  groupJumiaStock={
                    skuMode === 'by-group'
                      ? (groups.find(g => g._id === filterGroupId)?.stockAvailable ?? undefined)
                      : (skuMode === 'search-all' || skuMode === 'associated') && pgIsAssociated
                        ? ((pg as any)?.stockAvailable ?? undefined)
                        : undefined
                  }
                  groupManualStock={
                    skuMode === 'by-group'
                      ? (groups.find(g => g._id === filterGroupId)?.manualStock ?? undefined)
                      : (skuMode === 'associated' || skuMode === 'search-all') && pgIsAssociated
                        ? (groups.find(g => g._id?.toString() === pg?._id?.toString())?.manualStock ?? undefined)
                        : undefined
                  }
                  buyingRate={skuMode === 'by-group'
                    ? (groups.find(g => g._id === filterGroupId)?.buyingRate ?? undefined)
                    : pgBuyingRate}
                  groupOurFees={skuMode === 'by-group' ? (() => {
                    const g = groups.find(gr => gr._id === filterGroupId);
                    if (!g) return undefined;
                    const total = (g.transportFees || 0) + (g.storageFees || 0) + (g.packageFees || 0);
                    return total > 0 ? total : undefined;
                  })() : pgOurFees}
                  token={token}
                  onToggleSelect={() => toggleSelect(p._id)}
                  onAssign={() => setAssignTarget([p])}
                  onCreateGroup={() => setCreateTarget(p)}
                  onAssignSuggestion={(gId, gName) => doAssign(gId, gName, [p])}
                  onMove={() => doMoveSku(p._id)}
                  onRemove={() => doRemoveSku(p._id)}
                />
                );
              })}
            </div>
          </>
        )}

        </>}

        {/* ── Auto-Associate tab content ───────────────────────── */}
        {mainTab === 'auto' && (
          <div className="space-y-5">

            {/* Header card */}
            <div className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand/10">
                <Wand2 className="h-6 w-6 text-brand" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-bold text-foreground">Smart Auto-Associate</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Analyses product names and matches unassociated SKUs to existing groups.
                  Nothing is saved until you review and approve each suggestion.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(pendingHigh + pendingMedium) > 0 && !aaJobRunning && (
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={clearPending}>
                    <X className="h-3.5 w-3.5 mr-1" /> Clear all
                  </Button>
                )}
                <Button
                  size="sm"
                  className="bg-brand hover:bg-brand-dark text-white gap-1.5"
                  onClick={handleStartAutoAssociate}
                  disabled={aaJobRunning}
                >
                  <Wand2 className={cn('h-3.5 w-3.5', aaJobRunning && 'animate-pulse')} />
                  {aaJobRunning ? 'Analysing…' : (pendingHigh + pendingMedium) > 0 ? 'Re-run' : 'Run Auto-Associate'}
                </Button>
              </div>
            </div>

            {/* Job progress bar */}
            {aaJobRunning && aaJobState && (
              <div className="rounded-xl border border-brand/20 bg-brand/5 p-4 space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-brand">Analysing SKUs…</span>
                  <span className="text-muted-foreground">
                    {aaJobState.processed.toLocaleString()} / {aaJobState.total.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-brand/15 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand transition-all duration-500"
                    style={{ width: aaJobState.total > 0 ? `${Math.round((aaJobState.processed / aaJobState.total) * 100)}%` : '5%' }}
                  />
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>Matched: <strong className="text-foreground">{aaJobState.matched}</strong></span>
                  <span>High: <strong className="text-emerald-600">{aaJobState.high}</strong></span>
                  <span>Medium: <strong className="text-amber-600">{aaJobState.medium}</strong></span>
                </div>
              </div>
            )}

            {/* Results summary */}
            {!aaJobRunning && (pendingHigh + pendingMedium) > 0 && (
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-emerald-700">{pendingHigh} High Confidence</p>
                    <p className="text-[10px] text-emerald-600/70">Score ≥ 75% — very likely correct</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-700">{pendingMedium} Medium Confidence</p>
                    <p className="text-[10px] text-amber-600/70">Score 35–74% — review carefully</p>
                  </div>
                </div>
                <div className="flex-1" />
                {/* Apply button */}
                {Object.keys(localDecisions).length > 0 && (
                  <Button
                    size="sm"
                    className="bg-brand hover:bg-brand-dark text-white gap-1.5"
                    onClick={applyDecisions}
                    disabled={applying}
                  >
                    {applying
                      ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Applying…</>
                      : <><Check className="h-3.5 w-3.5" /> Apply {Object.keys(localDecisions).length} decision{Object.keys(localDecisions).length !== 1 ? 's' : ''}</>
                    }
                  </Button>
                )}
              </div>
            )}

            {/* Empty state after job with no matches */}
            {!aaJobRunning && aaJobState && !aaJobState.running && aaJobState.finishedAt && (pendingHigh + pendingMedium) === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                  <Package className="h-7 w-7 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-semibold text-foreground">No matches found</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  None of your unassociated SKUs had names similar enough to existing groups.<br/>
                  Try creating new groups first, then re-run.
                </p>
              </div>
            )}

            {/* Initial empty state (never run) */}
            {!aaJobRunning && !aaJobState && (pendingHigh + pendingMedium) === 0 && !pendingLoading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10">
                  <Wand2 className="h-7 w-7 text-brand/60" />
                </div>
                <p className="text-sm font-semibold text-foreground">Ready to analyse</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  Click "Run Auto-Associate" to scan all unassociated SKUs and find matches.
                  The job runs in the background — usually takes under a minute.
                </p>
              </div>
            )}

            {/* Review grid */}
            {(pendingHigh + pendingMedium) > 0 && !aaJobRunning && (
              <div className="space-y-4">
                {/* Confidence sub-tabs + bulk actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex rounded-lg border border-input bg-muted p-0.5 gap-0.5">
                    {(['High', 'Medium'] as const).map(conf => (
                      <button
                        key={conf}
                        onClick={() => setAaConfTab(conf)}
                        className={cn(
                          'rounded-md px-3 py-1 text-xs font-semibold transition-all flex items-center gap-1.5',
                          aaConfTab === conf ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <span className={cn(
                          'h-1.5 w-1.5 rounded-full',
                          conf === 'High' ? 'bg-emerald-500' : 'bg-amber-500'
                        )} />
                        {conf}
                        <span className="ml-0.5 text-[10px] text-muted-foreground">
                          ({conf === 'High' ? pendingHigh : pendingMedium})
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={acceptAllVisible}>
                    <CheckSquare className="h-3.5 w-3.5 text-emerald-600" /> Accept all {aaConfTab}
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={rejectAllVisible}>
                    <X className="h-3.5 w-3.5 text-red-500" /> Reject all {aaConfTab}
                  </Button>
                </div>

                {/* Cards */}
                {pendingLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="rounded-xl border border-border bg-card p-3 space-y-2">
                        <div className="flex gap-3">
                          <Skeleton className="h-16 w-16 rounded-lg shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <Skeleton className="h-3 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                          </div>
                          <Skeleton className="h-16 w-16 rounded-lg shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {pendingItems.filter(i => i.confidence === aaConfTab).map(item => {
                      const decision = localDecisions[item._id];
                      return (
                        <div
                          key={item._id}
                          className={cn(
                            'rounded-xl border bg-card p-3 space-y-3 transition-all',
                            decision === 'approved' && 'border-emerald-500/40 bg-emerald-500/5',
                            decision === 'rejected' && 'border-red-400/30 bg-red-500/5 opacity-60',
                            !decision && 'border-border hover:border-brand/30'
                          )}
                        >
                          {/* Product → Group visual */}
                          <div className="flex items-center gap-2">
                            {/* Product thumbnail */}
                            <div className="h-14 w-14 shrink-0 rounded-lg border border-border bg-muted overflow-hidden">
                              {item.imgUri
                                ? <img src={item.imgUri} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                : <div className="w-full h-full flex items-center justify-center"><Package className="h-6 w-6 text-muted-foreground/30" /></div>
                              }
                            </div>
                            {/* Arrow */}
                            <div className="flex flex-col items-center gap-0.5 shrink-0">
                              <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground/50" />
                              <span className={cn(
                                'text-[9px] font-bold px-1.5 py-0.5 rounded-full',
                                item.confidence === 'High'
                                  ? 'bg-emerald-500/15 text-emerald-700'
                                  : 'bg-amber-500/15 text-amber-700'
                              )}>
                                {Math.round(item.score * 100)}%
                              </span>
                            </div>
                            {/* Group thumbnail */}
                            <div className="h-14 w-14 shrink-0 rounded-lg border border-border bg-muted overflow-hidden">
                              {item.groupImageUrl
                                ? <img src={item.groupImageUrl} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                : <div className="w-full h-full flex items-center justify-center"><Layers className="h-6 w-6 text-muted-foreground/30" /></div>
                              }
                            </div>
                            <div className="flex-1 min-w-0 ml-1">
                              <p className="text-[10px] text-muted-foreground truncate">{item.sku}</p>
                              <p className="text-xs font-semibold text-foreground truncate leading-tight">{item.productName || item.sku}</p>
                              <p className="text-[10px] text-brand font-medium truncate mt-0.5">→ {item.groupName}</p>
                            </div>
                          </div>

                          {/* Matched words evidence */}
                          <div className="flex flex-wrap gap-1">
                            {item.matchedWords.slice(0, 5).map((w, i) => (
                              <span key={i} className="text-[9px] bg-brand/10 text-brand/80 font-semibold px-1.5 py-0.5 rounded-full">{w}</span>
                            ))}
                          </div>

                          {/* Accept / Reject */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleDecision(item._id, 'approved')}
                              className={cn(
                                'flex-1 flex items-center justify-center gap-1 rounded-lg border py-1.5 text-xs font-semibold transition-all',
                                decision === 'approved'
                                  ? 'bg-emerald-500 border-emerald-500 text-white'
                                  : 'border-input bg-background text-muted-foreground hover:border-emerald-500 hover:text-emerald-600'
                              )}
                            >
                              <Check className="h-3 w-3" /> Accept
                            </button>
                            <button
                              onClick={() => toggleDecision(item._id, 'rejected')}
                              className={cn(
                                'flex-1 flex items-center justify-center gap-1 rounded-lg border py-1.5 text-xs font-semibold transition-all',
                                decision === 'rejected'
                                  ? 'bg-red-500 border-red-500 text-white'
                                  : 'border-input bg-background text-muted-foreground hover:border-red-400 hover:text-red-500'
                              )}
                            >
                              <X className="h-3 w-3" /> Reject
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Sticky apply bar */}
                {Object.keys(localDecisions).length > 0 && (
                  <div className="sticky bottom-4 flex justify-center pointer-events-none">
                    <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-brand/30 bg-card/95 backdrop-blur shadow-lg px-5 py-3">
                      <span className="text-xs text-muted-foreground">
                        <strong className="text-foreground">{Object.values(localDecisions).filter(d => d === 'approved').length}</strong> accepted ·{' '}
                        <strong className="text-foreground">{Object.values(localDecisions).filter(d => d === 'rejected').length}</strong> rejected
                      </span>
                      <Button
                        size="sm"
                        className="bg-brand hover:bg-brand-dark text-white gap-1.5"
                        onClick={applyDecisions}
                        disabled={applying}
                      >
                        {applying
                          ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Applying…</>
                          : <><Check className="h-3.5 w-3.5" /> Apply {Object.keys(localDecisions).length} decisions</>
                        }
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* ── Product Groups tab content ────────────────────────── */}
        {mainTab === 'groups' && loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border bg-card overflow-hidden">
                <Skeleton className="h-24 w-full rounded-none" />
                <div className="p-2.5 space-y-1.5">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}
        {mainTab === 'groups' && !loading && groups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10">
              <Layers className="h-8 w-8 text-brand" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-bold text-foreground">No product groups yet</h3>
              <p className="text-sm text-muted-foreground mt-1">Create a group from the SKU Association tab to get started.</p>
            </div>
          </div>
        )}
        {mainTab === 'groups' && !loading && groups.length > 0 && (
          <div>
            {/* Groups header with search + sort */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
                <Layers className="h-4 w-4 text-brand" />
                Product Groups ({groups.length})
              </h2>
              <div className="flex-1" />
              {/* Group search */}
              <div className="flex items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 py-1 min-w-[160px]">
                <Search className="h-3 w-3 text-muted-foreground shrink-0" />
                <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
                  placeholder="Search groups…"
                  className="flex-1 text-xs bg-transparent outline-none placeholder:text-muted-foreground" />
                {groupSearch && <button onClick={() => setGroupSearch('')}><X className="h-3 w-3 text-muted-foreground" /></button>}
              </div>
              {/* Group sort */}
              <div className="relative">
                <select value={groupSort} onChange={e => setGroupSort(e.target.value as any)}
                  className="appearance-none rounded-lg border border-input bg-background pl-2.5 pr-6 py-1 text-xs outline-none focus:border-ring cursor-pointer text-foreground">
                  <option value="orders">Orders ↓</option>
                  <option value="revenue">Revenue ↓</option>
                  <option value="manualStock">Manual Stock ↓</option>
                  <option value="jumiaStock">Jumia Stock ↓</option>
                  <option value="skuCount">SKUs ↓</option>
                  <option value="name">Name A–Z</option>
                </select>
                <SortDesc className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
              </div>
              <span className="text-xs text-muted-foreground">{visibleGroups.length} groups</span>
            </div>

            {/* Multi-select merge bar */}
            {selectedGroupIds.size >= 2 && (
              <div className="flex items-center gap-2 mb-3 p-3 rounded-xl border border-brand bg-brand/5">
                <span className="text-sm font-semibold text-brand">{selectedGroupIds.size} groups selected</span>
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => setSelectedGroupIds(new Set())} className="h-7 text-xs">Deselect all</Button>
                <Button size="sm" className="h-7 text-xs bg-brand hover:bg-brand-dark text-white"
                  onClick={() => setShowMergeGroups(true)}>
                  Merge {selectedGroupIds.size} Groups
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {visibleGroups.map(g => (
                <div
                  key={g._id}
                  className={cn(
                    'flex flex-col rounded-xl border bg-card hover:border-brand hover:shadow-card-md transition-all duration-150 overflow-hidden text-left group relative',
                    selectedGroupIds.has(g._id) && 'border-brand ring-2 ring-brand/20'
                  )}
                >
                  {/* Checkbox */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedGroupIds(prev => {
                        const n = new Set(prev);
                        n.has(g._id) ? n.delete(g._id) : n.add(g._id);
                        return n;
                      });
                    }}
                    className="absolute top-2 left-2 z-10 p-0.5 rounded-md bg-card/90 backdrop-blur-sm border border-border hover:border-brand transition-colors"
                  >
                    {selectedGroupIds.has(g._id)
                      ? <CheckSquare className="h-4 w-4 text-brand" />
                      : <Square className="h-4 w-4 text-muted-foreground" />}
                  </button>

                  {/* Image area — clickable to open detail */}
                  <div className="h-24 bg-muted flex items-center justify-center overflow-hidden cursor-pointer"
                    onClick={() => setDetailGroup(g)}
                    onMouseEnter={e => handleGroupImgEnter(e, g)}
                    onMouseLeave={() => setGroupImgPopup(null)}
                  >
                    {g.imageUrl
                      ? <img src={g.imageUrl} alt={g.name} className="w-full h-full object-cover cursor-zoom-in" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : <Layers className="h-8 w-8 text-muted-foreground/30" />}
                  </div>

                  {/* Card body — clickable to open detail */}
                  <div className="p-2.5 space-y-0.5 cursor-pointer" onClick={() => setDetailGroup(g)}>
                    <p className="text-xs font-semibold text-foreground truncate">{g.name}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-muted-foreground">{g.skuCount} SKU{g.skuCount !== 1 ? 's' : ''}</span>
                      {(g.deliveredOrders || 0) > 0 && (
                        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-500/10 rounded-full px-1.5">
                          {g.deliveredOrders} ✓
                        </span>
                      )}
                    </div>
                    {/* Stock row */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(g.stockAvailable ?? 0) > 0 && (
                        <span className="text-[10px] font-semibold text-blue-600 bg-blue-500/10 rounded-full px-1.5">
                          📦 {g.stockAvailable} Jumia
                        </span>
                      )}
                      {g.manualStock !== null && g.manualStock !== undefined && (
                        <span className="text-[10px] font-semibold text-violet-600 bg-violet-500/10 rounded-full px-1.5">
                          🏭 {g.manualStock} manual
                        </span>
                      )}
                    </div>
                    {g.category && <p className="text-[10px] text-brand/80 font-medium truncate">{g.category}</p>}
                    {(g.revenue || 0) > 0 && (
                      <p className="text-[10px] font-semibold text-foreground/60">{Math.round(g.revenue!).toLocaleString()} MAD</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────── */}

      {(assignTarget || moveTarget) && (
        <AssignModal
          products={assignTarget || []}
          groups={groups}
          onConfirm={(gId, gName) => {
            if (moveTarget) { doMoveConfirm(gId, gName); }
            else if (assignTarget) { doAssign(gId, gName, assignTarget); }
          }}
          onClose={() => { setAssignTarget(null); setMoveTarget(null); }}
        />
      )}

      {createTarget !== null && (
        <CreateGroupModal
          prefillProduct={Array.isArray(createTarget) ? undefined : createTarget as UnassociatedProduct}
          prefillProducts={Array.isArray(createTarget) && (createTarget as UnassociatedProduct[]).length > 0
            ? createTarget as UnassociatedProduct[]
            : undefined}
          token={token}
          onCreated={(gId, gName) => {
            setCreateTarget(null);
            setSelectedIds(new Set());
            addToast('success', `Group "${gName}" created`);
            load(true);
          }}
          onClose={() => setCreateTarget(null)}
        />
      )}

      {detailGroup && (
        <GroupDetailPanel
          group={detailGroup}
          onClose={() => setDetailGroup(null)}
          onRemoveSku={doRemoveSku}
          onMoveSku={doMoveSku}
          onSetImageFromSku={doSetImageFromSku}
          onGroupUpdated={(updated) => {
            setDetailGroup(updated);
            setGroups(prev => prev.map(g => g._id === updated._id ? { ...g, ...updated } : g));
          }}
          token={token}
        />
      )}

      {showHistory && (
        <HistoryDrawer
          history={history}
          loading={historyLoading}
          onClose={() => setShowHistory(false)}
        />
      )}

      {showMergeGroups && (
        <MergeGroupsModal
          groups={groups
            .filter(g => selectedGroupIds.has(g._id))
            .map(g => ({
              _id: g._id,
              name: g.name,
              imageUrl: g.imageUrl,
              skuCount: g.skuCount,
              buyingRate: g.buyingRate,
              transportFees: g.transportFees,
              storageFees: g.storageFees,
              packageFees: g.packageFees,
              jumiaCommission: g.jumiaCommission,
              fraisTraitement: g.fraisTraitement,
              category: g.category,
              supplier: g.supplier,
              notes: g.notes,
            } as MergeableGroup))}
          token={token}
          onMerged={() => { setShowMergeGroups(false); setSelectedGroupIds(new Set()); load(true); }}
          onClose={() => setShowMergeGroups(false)}
        />
      )}

      {/* Group image hover popup */}
      {groupImgPopup && groupImgPopup.g.imageUrl && (
        <div
          className="animate-fade-in rounded-xl overflow-hidden shadow-dropdown border border-border bg-card pointer-events-none"
          style={{ position: 'fixed', top: groupImgPopup.top, left: groupImgPopup.left, width: 220, zIndex: 999 }}
        >
          <img
            src={groupImgPopup.g.imageUrl}
            alt={groupImgPopup.g.name}
            style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block', background: 'hsl(var(--muted))' }}
            onError={() => setGroupImgPopup(null)}
          />
          <div className="px-3 py-2 border-t border-border">
            <p className="text-xs font-semibold text-foreground truncate">{groupImgPopup.g.name}</p>
            {groupImgPopup.g.category && <p className="text-[10px] text-brand/80 font-medium">{groupImgPopup.g.category}</p>}
            <p className="text-[10px] text-muted-foreground">{groupImgPopup.g.skuCount} SKU{groupImgPopup.g.skuCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkuAssociationPage;
